import React from 'react';
import NProgress from 'nprogress';
import { Card } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { validateString } from 'avilatek-utils';
import { FormButtons, FormHeader } from './shared/Forms';
import { TextAreaEditor } from './shared';
import { CREATE_SETTINGS, UPDATE_SETTINGS } from '../graphql/mutation';
import { Settings } from '../models';
import { useNotify } from '../hooks';
import { removeProperties } from '../lib/removeProperties';

interface TermAndConditionsProps {
  settings?: Settings;
}

function TermAndConditions({ settings }: TermAndConditionsProps) {
  const notify = useNotify();
  const [disabled, setDisabled] = React.useState(false);
  const [termsAndConditions, setTermsAndConditions] = React.useState(
    settings.termsAndConditions ?? ''
  );

  const [updateSettingsMutation] = useMutation<{
    updateSettings: { record: Settings };
  }>(UPDATE_SETTINGS);

  const [createSettingsMutation] = useMutation<{
    createSettings: { record: Settings };
  }>(CREATE_SETTINGS);

  const createSettings = async (settingsData: Partial<Settings>) => {
    const { data } = await createSettingsMutation({
      variables: {
        record: {
          ...settingsData,
        },
      },
    });
    if (data?.createSettings.record?._id) {
      notify('Terminos y condiciones creado con éxito', 'success');
    } else {
      notify(
        'Ha ocurrido un error al crear los terminos y condiciones',
        'error'
      );
    }
  };

  const updateSettings = async (settingsData: Partial<Settings>) => {
    const { data } = await updateSettingsMutation({
      variables: {
        filter: {
          _id: settings._id,
        },
        record: {
          ...settingsData,
          _id: undefined,
          __typename: undefined,
        },
      },
    });
    if (data?.updateSettings.record?._id) {
      notify('Terminos y condiciones actualizadas con éxito', 'success');
    } else {
      notify(
        'Ha ocurrido un error al actualizar los terminos y condiciones',
        'error'
      );
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      if (!termsAndConditions || !validateString(termsAndConditions)) {
        return notify('Debe ingresar los terminos y condiciones', 'error');
      }
      // create or update
      if (settings?._id) {
        await updateSettings(
          removeProperties({
            ...settings,
            termsAndConditions,
          })
        );
      } else {
        await createSettings({
          termsAndConditions,
        });
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader title="Actualizar terminos y condiciones" />
        <Card.Body>
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <label className="block">
                <span className="text-gray-300 text-sm font-arial font-extrabold mb-2 tracking-wider ">
                  Ingresa los terminos y condiciones
                </span>
                <TextAreaEditor
                  value={termsAndConditions}
                  onChange={(_text) => {
                    setTermsAndConditions(_text);
                  }}
                />
              </label>
            </div>
          </div>
        </Card.Body>
      </Card>
      <FormButtons
        type="update"
        disabled={disabled}
        onSubmit={onSubmit}
        active
      />
    </form>
  );
}

export default TermAndConditions;
